import {
  Box,
  Divider,
  Flex,
  SimpleGrid,
  Spacer,
  Stack,
} from '@chakra-ui/react';
import { SearchClient } from 'algoliasearch';
import * as React from 'react';
import { Configure } from 'react-instantsearch-dom';

import { MoreFilters } from '@/components/MoreFilters';
import { SelectedFilters } from '@/components/SelectedFilters';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import { useFilterableAttributes } from '@/hooks/useFilterableAttributes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { Pagination } from '@/imports/ui/components/algolia/Pagination';
import { SearchBox } from '@/imports/ui/components/algolia/SearchBox';
import { SearchTips } from '@/imports/ui/components/algolia/SearchTips';
import { StateResults } from '@/imports/ui/components/algolia/StateResults/StateResults';
import { InstantSearchWithErrorLogging } from '@/imports/ui/components/search/InstantSearchWithErrorLogging';
import { useMemberDirectory } from '@/imports/ui/hooks/useMemberDirectory';
import {
  Strawberry,
  StrawberryAside,
  StrawberryMain,
} from '@/layouts/Strawberry';

import { Hits } from './components/Hits/Hits';
import { useSearchableUsersCount } from './hooks/useSearchableUsersCount';

const _MemberDirectoryPage: React.FC<{
  indexName: string;
  searchClient: SearchClient | null;
  searchableAttributesField?: string;
}> = ({ indexName, searchClient, searchableAttributesField }) => {
  const { searchableUsersCount } = useSearchableUsersCount();
  const {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter,
    isLoadingFilters,
    facetFilters,
  } = useFilterableAttributes({
    indexName,
    searchClient,
    searchableAttributesField,
  });

  const memberManagement = useMemberManagement();

  if (isLoadingFilters || memberManagement.loading) {
    return <Loading />;
  }

  return (
    <InstantSearchWithErrorLogging
      indexName={indexName}
      searchClient={searchClient}
    >
      <Configure hitsPerPage={40} facetFilters={facetFilters} />
      <Stack spacing="4">
        <Card>
          <Stack spacing="0" divider={<Divider />}>
            <Flex py="4" pl="4">
              <SearchBox placeholder="Search people" />
              <SearchTips ownerKind={EAttributeOwnerKind.USER} />
            </Flex>

            {(selectedFilters.length > 0 || unselectedFilters.length > 0) && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4" p="4">
                <SelectedFilters
                  selectedFilters={selectedFilters}
                  unselectFilter={unselectFilter}
                  selectFilter={selectFilter}
                />

                <Flex direction="column">
                  <Spacer />
                  <MoreFilters
                    unselectedFilters={unselectedFilters}
                    selectFilter={selectFilter}
                  />
                </Flex>
              </SimpleGrid>
            )}
          </Stack>
        </Card>

        <Card px="4" py="6" pos="relative">
          <StateResults
            preciseNumHitsWithNoQuery={
              //searchableUsersCount is not reliable when member managment is turned on for a tenant, so we can only rely on algolia's count
              memberManagement.isActive ? undefined : searchableUsersCount
            }
            someFilterIsApplied={facetFilters.length > 0}
          />
          <Hits facetFilters={facetFilters} />
        </Card>

        <Box
          w="100%"
          overflowX="auto"
          pb={{ base: '72px', sm: '72px', md: '72px', lg: '0' }}
          pt={1}
        >
          <Pagination />
        </Box>
      </Stack>
    </InstantSearchWithErrorLogging>
  );
};
export const MemberDirectoryPage: React.FC = () => {
  const { loading, indexName, searchClient, searchableAttributesField } =
    useMemberDirectory();

  return (
    <Strawberry>
      <StrawberryMain>
        {loading ? (
          <Loading />
        ) : !indexName ? (
          <UnexpectedError />
        ) : (
          <_MemberDirectoryPage
            indexName={indexName}
            searchClient={searchClient}
            searchableAttributesField={searchableAttributesField}
          />
        )}
      </StrawberryMain>
      <StrawberryAside />
    </Strawberry>
  );
};
